import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Col, Form, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { useCreateUserMutation } from '../../../pages/authentication/data-access/+state/user.api';

const SignUpForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const navigate = useNavigate();
  const [createUser, { isLoading, isSuccess }] = useCreateUserMutation();

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [serverError, setServerError] = useState('');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [fieldError, setFieldError] = useState<{ [key: string]: string }>({});

  const isValidEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSignUp = async () => {
    setFieldError({});
    setServerError('');

    if (!username) {
      setFieldError(prev => ({ ...prev, username: 'Email is required' }));
      return;
    }

    if (!isValidEmail(username)) {
      setFieldError(prev => ({
        ...prev,
        username: 'Please enter a valid email'
      }));
      return;
    }

    if (!password) {
      setFieldError(prev => ({ ...prev, password: 'Password is required' }));
      return;
    }

    if (password !== confirmPassword) {
      setFieldError(prev => ({
        ...prev,
        confirmPassword: 'Passwords do not match'
      }));
      return;
    }

    try {
      await createUser({ username, password, companyName }).unwrap();
    } catch (error: any) {
      if (error?.data?.message) {
        setServerError(error.data.message); // e.g., "Username already exists"
      } else {
        setServerError('Something went wrong. Please try again.');
      }
      setShowErrorSnackbar(true);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      setShowSuccessDialog(true);
    }
  }, [isSuccess]);

  const handleDialogClose = () => {
    setShowSuccessDialog(false);
    navigate('/');
  };

  return (
    <>
      <div className="text-center mb-7">
        <h3 className="text-body-highlight">Sign Up</h3>
        <p className="text-body-tertiary">Create your account today</p>
      </div>

      <div className="position-relative mt-4">
        <hr className="bg-body-secondary" />
        <div className="divider-content-center">or use email</div>
      </div>

      <Form>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="name">Name</Form.Label>
          <Form.Control
            onChange={e => setCompanyName(e.target.value)}
            id="name"
            type="text"
            placeholder="Name"
          />
        </Form.Group>

        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="email">Email address</Form.Label>
          <Form.Control
            id="email"
            type="email"
            value={username}
            onChange={e => {
              setUsername(e.target.value);
              setFieldError(prev => ({ ...prev, username: '' }));
            }}
            placeholder="name@example.com"
            isInvalid={!!fieldError.username}
          />
          <Form.Control.Feedback type="invalid">
            {fieldError.username}
          </Form.Control.Feedback>
        </Form.Group>

        <Row className="g-3 mb-3">
          <Col sm={layout === 'card' ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="password">Password</Form.Label>
              <Form.Control
                id="password"
                type="password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  setFieldError(prev => ({ ...prev, password: '' }));
                }}
                placeholder="Password"
                isInvalid={!!fieldError.password}
              />
              <Form.Control.Feedback type="invalid">
                {fieldError.password}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col sm={layout === 'card' ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="confirmPassword">
                Confirm Password
              </Form.Label>
              <Form.Control
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={e => {
                  setConfirmPassword(e.target.value);
                  setFieldError(prev => ({ ...prev, confirmPassword: '' }));
                }}
                placeholder="Confirm Password"
                isInvalid={!!fieldError.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                {fieldError.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Form.Check type="checkbox" className="mb-3">
          <Form.Check.Input
            type="checkbox"
            name="termsService"
            id="termsService"
          />
          <Form.Check.Label
            htmlFor="termsService"
            className="fs-9 text-transform-none"
          >
            I accept the <Link to="#!">terms</Link> and{' '}
            <Link to="#!">privacy policy</Link>
          </Form.Check.Label>
        </Form.Check>

        {serverError && (
          <div className="text-danger text-center mb-3">{serverError}</div>
        )}

        <Button
          onClick={handleSignUp}
          variant="primary"
          className="w-100 mb-3"
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Sign Up'
          )}
        </Button>

        <div className="text-center">
          <Link to={`/`} className="fs-9 fw-bold">
            Sign in to an existing account
          </Link>
        </div>
      </Form>

      {/* ✅ Success Modal */}
      <Dialog open={showSuccessDialog} onClose={handleDialogClose}>
        <DialogTitle>Account Created</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your account has been created successfully.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleDialogClose} autoFocus>
            Okay
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* ❌ Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowErrorSnackbar(false)}
          severity="error"
          variant="filled"
        >
          {serverError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SignUpForm;
